/**
 * Gated HITL bridges for `propose_contour_model` and `start_auto_segmentation`.
 *
 * Same gate as the structure operations (≤1-pending preamble, recoverable
 * `failed` outcomes the model re-asks against, Accept/Reject card): parse the
 * model's input → check the model exists in the Contour+ list → a
 * {@link ModelProposal} the card renders. Nothing happens until the clinician
 * Accepts. Accept hands the choice to the Contour+ form through
 * `requestForm`; the form's own code selects the model and, for a start,
 * submits it. Pure/React-free.
 */
import type { AgentContourModelRequest } from '~/state/atoms/chatAtoms';
import type { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
} from '../core';
import type {
    ModelActionOutcome,
    ProposeContourModelInput,
} from '../../contract';

export type ModelAction = 'select' | 'start';

export interface ModelSelectionDeps extends PendingFlag {
    /** The Contour+ model list the manual form uses; undefined if not loaded. */
    contourModels(): ReadonlyArray<PredictionModel> | undefined;
    /** Hand an accepted choice to the Contour+ form. */
    requestForm(request: AgentContourModelRequest): void;
    /** Open the Contour+ form so it can submit the accepted start request. */
    openContourForm(): void;
}

export interface ModelProposal {
    readonly toolCallId: string;
    readonly action: ModelAction;
    readonly modelName: string;
    readonly modelLabel: string;
    readonly bodyPart: string;
    readonly customizationName?: string;
    apply(): Promise<ModelActionOutcome>;
    reject(): ModelActionOutcome;
    /** No viewport overlay for a model choice; kept for the shared channel. */
    showPreview(): void;
    hidePreview(): void;
}

export type ModelPlan = MutatingPlan<ModelProposal>;

function readInput(input: unknown): ProposeContourModelInput | undefined {
    const o = asRecord(input);
    if (o === undefined || typeof o.modelName !== 'string' || !o.modelName) {
        return undefined;
    }
    if (
        o.customizationName !== undefined &&
        typeof o.customizationName !== 'string'
    ) {
        return undefined;
    }
    return {
        modelName: o.modelName,
        ...(o.customizationName
            ? { customizationName: o.customizationName }
            : {}),
    };
}

function hasCustomization(model: PredictionModel, name: string): boolean {
    return (model.customizations ?? []).some(
        (c) => c.name !== model.name && c.name.split('.').pop() === name
    );
}

function planModelAction(
    action: ModelAction,
    toolName: string,
    call: MutatingToolCall,
    deps: ModelSelectionDeps
): ModelPlan {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const parsed = readInput(call.input);
    if (parsed === undefined) {
        return failedPlan(call.toolCallId, `invalid ${toolName} input`);
    }
    const models = deps.contourModels();
    if (!models) {
        return failedPlan(
            call.toolCallId,
            'the Contour+ model list is not loaded yet'
        );
    }
    const model = models.find((m) => m.name === parsed.modelName);
    if (!model) {
        return failedPlan(
            call.toolCallId,
            `"${parsed.modelName}" is not an available Contour+ model; call ` +
                'analyze_image_for_model_selection and use a model it returned'
        );
    }
    const { customizationName } = parsed;
    if (customizationName && !hasCustomization(model, customizationName)) {
        return failedPlan(
            call.toolCallId,
            `"${customizationName}" is not a customization of ` +
                `"${model.name}"; omit it for the default`
        );
    }

    const request: AgentContourModelRequest = {
        modelName: model.name,
        ...(customizationName ? { customizationName } : {}),
        autoStart: action === 'start',
    };
    const proposal: ModelProposal = {
        toolCallId: call.toolCallId,
        action,
        modelName: model.name,
        modelLabel: model.label,
        bodyPart: model.bodyPart,
        ...(customizationName ? { customizationName } : {}),
        apply: async () => {
            deps.requestForm(request);
            if (action === 'start') {
                deps.openContourForm();
            }
            return {
                status: 'applied',
                modelName: model.name,
                ...(customizationName ? { customizationName } : {}),
            };
        },
        reject: () => ({ status: 'rejected' }),
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}

export async function planProposeContourModel(
    call: MutatingToolCall,
    deps: ModelSelectionDeps
): Promise<ModelPlan> {
    return planModelAction('select', 'propose_contour_model', call, deps);
}

export async function planStartAutoSegmentation(
    call: MutatingToolCall,
    deps: ModelSelectionDeps
): Promise<ModelPlan> {
    return planModelAction('start', 'start_auto_segmentation', call, deps);
}
